import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import arrowIcon from "../../../assets/previous_icon.png";
import editIcon from "../../../assets/edit_icon.png";
import closeIcon from "../../../assets/close_icon.png";
import saveIcon from "../../../assets/save_icon.png";
import checkIcon from "../../../assets/check_icon.png";
import printIcon from "../../../assets/printer_icon.png";

import CustomButton from "../../../components/CustomButton";
import AreYouSureModal from "../../../components/AreYouSureModal";
import { useNavigate } from "react-router-dom";
import CustomInputWithUnit from "../../../components/CustomInputWithUnit";
import { useReactToPrint } from "react-to-print";
import useLanguage from "../../../utils/useLanguage";
import PrintableUniformOrderView from "./PrintableUniformOrder";
import CustomCheckbox from "../../../components/CustomCheckbox";
import axiosClient from "../../../services/axiosClient";
import { updateProductOrder } from "../../../services/productServices";
import CustomInput from "../../../components/CustomInput";
import Loader from "../../../components/Loader";
import HotdropdownV2 from "../../../components/HotdropdownV2";
import { getAllKassas } from "../../../services/kassa/kassaServices";
import { useContext } from "react";
import { UserAuth } from "../../../utils/AuthContext";

const UniformOrders = ({
  data,
  setData,
  orderStatus = "PENDING",
  setRefetch,
}) => {
  // HOOKS
  const { t } = useTranslation("general");
  const navigate = useNavigate();
  const { currentDirection } = useLanguage();

  // STATES
  const [selectedProducts, setSelectedProducts] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [areYouSureModal, setAreYouSureModal] = useState(false);
  const printRef = useRef();
  const [selectedCurrency] = useState({ title: "iqd", value: "IQD" });
  const [editableNotes, setEditableNotes] = useState();
  const [kassaLoading, setKassasLoading] = useState(false);
  const [kassas, setKassas] = useState([]);
  const [selectedKassa, setSelectedKassa] = useState(null);
  const [error, setError] = useState(null);
  const { userData } = useContext(UserAuth);

  // Transform backend response - Group by product type and name as ARRAY
  const transformedData =
    data?.at(0)?.ProductOrderItems?.reduce((acc, item) => {
      const productName = item.productRulerItem?.productRuler?.name;
      const productType = item.productRulerItem?.productRuler?.color;

      if (!productName) return acc;

      const groupKey = `${productType}_${productName}`;

      // Find existing group in array
      let group = acc.find((g) => g.groupKey === groupKey);

      if (!group) {
        group = {
          groupKey: groupKey,
          name: productName,
          color: productType,
          sizes: [],
        };
        acc.push(group);
      }

      group.sizes.push({
        id: item.id,
        productId: item.productId,
        productRulerItemId: item.productRulerItemId,
        size: item.productRulerItem?.size,
        requestedQuantity: item.requestedQuantity || "",
        quantity: item?.quantity || "",
        price: item.price,
        totalPrice: item.totalPrice,
        averagePrice: item.productRulerItem?.averagePrice,
        purchasePrice: item.productRulerItem?.purchasePrice,
        amountInStock: item.productRulerItem?.amountInStock,
        unitOfMeasure: item.productRulerItem?.unitOfMeasure,
      });

      return acc;
    }, []) || [];

  const productList =
    isEditMode && editableData ? editableData : transformedData;

  const currentOrder = Array.isArray(data) && data.length > 0 ? data[0] : null;
  const productOrderItems = currentOrder?.ProductOrderItems || [];

  const handleCheckboxChange = (groupKey, variantId) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [groupKey]: {
        ...prev[groupKey],
        [variantId]: !prev[groupKey]?.[variantId],
      },
    }));
  };

  const toggleCollapse = (groupKey) => {
    setCollapsed((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const handleEdit = () => {
    const prechecked = {};
    const deepCopy = transformedData.map((productGroup) => {
      prechecked[productGroup.groupKey] = {};

      const sizes = productGroup.sizes.map((variant) => {
        prechecked[productGroup.groupKey][variant.id] = true;
        return { ...variant };
      });

      return {
        groupKey: productGroup.groupKey,
        name: productGroup.name,
        color: productGroup.color,
        sizes: sizes,
      };
    });

    setSelectedProducts(prechecked);
    setEditableData(deepCopy);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditableData(null);
    setSelectedProducts({});
  };

  const handleSave = async () => {
    if (!editableData || !currentOrder) return;

    setLoading(true);
    try {
      const updatedItems = editableData
        .map((item) => {
          return item.sizes.map((size) => {
            if (!selectedProducts[item.groupKey]?.[size.id]) {
              return null;
            }
            return {
              kassaId: selectedKassa?.value || currentOrder.kassaId,
              productRulerItemId: size.productRulerItemId,
              price: parseFloat(size.price || 0),
              quantity: parseInt(size.quantity) || null,
              requestedQuantity: parseInt(size.requestedQuantity),
              totalPrice: parseFloat(size.totalPrice) || 0,
              ruler: true,
            };
          });
        })
        .flat()
        .filter(Boolean);

      const updateData = {
        kassaId: selectedKassa?.value || currentOrder.kassaId,
        orderNumber: currentOrder.orderNumber,
        orders: updatedItems,
        notes: editableNotes || currentOrder.notes,
        status: "PENDING",
      };

      const response = await updateProductOrder(updateData);

      if (response.status === 200) {
        const newOrder = response.data;
        setRefetch((prev) => !prev);
        setIsEditMode(false);
        setEditableData(null);
        setSelectedProducts({});
      }
    } catch (error) {
      console.error("Error saving order edits:", error);
      setError("failedToSaveOrder");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (groupKey, variantId, field, value) => {
    setEditableData((prev) => {
      return prev.map((group) => {
        if (group.groupKey !== groupKey) return group;

        const sizes = group.sizes.map((variant) => {
          if (variant.id !== variantId) {
            return variant;
          }

          const price = field === "price" ? value : variant.price || 0;
          const quantity = field === "quantity" ? value : variant.quantity || 0;
          const totalPrice =
            field === "totalPrice"
              ? value
              : parseFloat((price * quantity).toFixed(3));
          return { ...variant, [field]: value, totalPrice };
        });

        return {
          ...group,
          sizes,
        };
      });
    });
  };

  const handleConfirmOrder = async () => {
    if (!currentOrder) return;

    setLoading(true);
    const updatedItems = productOrderItems.map((item) => {
      return {
        kassaId: selectedKassa?.value || currentOrder.kassaId,
        productRulerItemId: item.productRulerItemId,
        price: parseFloat(item.price || 0),
        quantity: parseInt(item.quantity),
        requestedQuantity: parseInt(item.requestedQuantity),
        totalPrice: parseFloat(item.totalPrice) || 0,
        ruler: true,
      };
    });

    try {
      const updateData = {
        orderNumber: currentOrder.orderNumber,
        kassaId: currentOrder.kassaId,
        status: "CONFIRMED",
        notes: currentOrder.notes,
        date: currentOrder.date,
        orders: updatedItems,
      };

      const response = await updateProductOrder(updateData);

      if (response.status === 200) {
        setAreYouSureModal(false);
        const newOrder = response.data;
        // Correctly update the state to an array containing the new order.
        setData([newOrder]);
        navigate(-1);
      }
    } catch (error) {
      console.error("Error confirming order:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttemptToConfirmReceived = () => setAreYouSureModal(true);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
          size: A4;
          margin: 5mm;
          width: 21cm;
          height: 29.7cm;
          direction: ${currentDirection}
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          padding: 10mm;
        }
        .printable-order-view {
          display: flex !important;
          direction: ${currentDirection}
        }
      }
    `,
  });

  const orderDetails = {
    orderNumber: currentOrder?.orderNumber,
    date: currentOrder?.date,
    companyName: currentOrder.creator?.organization?.organizationName,
    createdBy: currentOrder.creator?.username,
    notes: currentOrder?.notes || "",
  };

  const canConfirmOrder = () => {
    return productOrderItems.every((item) => {
      const hasQuantity = item.quantity && parseInt(item.quantity) > 0;
      const hasTotalPrice = item.totalPrice && parseFloat(item.totalPrice) > 0;
      return hasQuantity && hasTotalPrice;
    });
  };

  const fetchKassas = async () => {
    setKassasLoading(true);
    try {
      const response = await getAllKassas({
        page: 1,
        limit: 100,
      });

      if (response.status === 200) {
        setKassas(response.data.kassas);

        // Set first kassa as default if available
        if (response.data.kassas.length > 0) {
          const firstKassa = response.data.kassas[0];
          setSelectedKassa({
            title: firstKassa.name,
            value: firstKassa.id,
            kassaData: firstKassa,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching Kassas:", error);
      setError("failedToFetchKassas");
    } finally {
      setKassasLoading(false);
    }
  };

  const transformKassasForDropdown = (kassasData) => {
    return kassasData.map((kassa) => ({
      title: kassa.name,
      value: kassa.id,
      kassaData: kassa,
    }));
  };

  useEffect(() => {
    if (orderStatus === "PENDING") {
      fetchKassas();
    }
  }, []);

  return (
    <div className="c-fabric-table">
      <div className="o-row o-row--horizontal-align_space-between">
        <h1 className="t-headline-1">
          {t("orderNumber")}: {currentOrder.orderNumber}
        </h1>
      </div>

      {/* Notes Section */}
      <div className="o-row u-margin--top_300 u-margin--bottom_300">
        <div
          className="o-column o-column--gap-size_100"
          style={{ width: "100%" }}
        >
          <label className="t-bold_bold">{t("notes")}:</label>
          {isEditMode ? (
            <CustomInput
              type="textarea"
              value={editableNotes}
              onChange={(e) => setEditableNotes(e.target.value)}
            />
          ) : (
            <div>{currentOrder.notes || t("noNotes")}</div>
          )}
        </div>
      </div>

      <div className="o-row o-row--horizontal-align_space-between o-row--vertical-align_end u-margin--top_300 u-margin--bottom_600">
        {orderStatus === "PENDING" &&
          (kassaLoading ? (
            <Loader size="small" />
          ) : kassas.length > 0 ? (
            <>
              <div className="o-column o-column--gap-size_100">
                <label className="t-bold_bold">{t("selectKassa")}:</label>
                <HotdropdownV2
                  initOptions={transformKassasForDropdown(kassas)}
                  selected={selectedKassa?.title || ""}
                  setSelected={(option) => setSelectedKassa(option)}
                  optionsName="kassas"
                  thick
                />
              </div>
              {orderStatus === "PENDING" && (
                <>
                  {!isEditMode ? (
                    <CustomButton
                      variant="blank"
                      title={t("edit")}
                      onClick={handleEdit}
                      icon={editIcon}
                      iconSize={18}
                    />
                  ) : (
                    <div className="o-row o-row--gap-size_300">
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleCancel}
                        variant="blank"
                        icon={closeIcon}
                        iconSize={14}
                      />
                      <CustomButton
                        title={loading ? t("saving") : t("save")}
                        onClick={handleSave}
                        icon={saveIcon}
                        iconSize={16}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <span className="u-color_error">{t("noKassasAvailable")}</span>
          ))}
      </div>

      <div className="o-column o-column--gap-size_300">
        {productList.map((productGroup, index) => {
          const groupKey = productGroup.groupKey;
          return (
            <div key={groupKey} className="c-fabric-block">
              <div
                className="c-fabric-header"
                onClick={() => toggleCollapse(groupKey)}
              >
                <span
                  className="c-collapse-icon"
                  style={{
                    cursor: "pointer",
                    transform: collapsed[groupKey]
                      ? "rotate(0deg)"
                      : "rotate(-90deg)",
                    transition: "transform 0.3s ease",
                  }}
                >
                  <img src={arrowIcon} alt="Collapse" style={{ width: 16 }} />
                </span>
                <div className="o-column o-column--gap-size_200">
                  <span className="c-fabric-code">
                    {t("uniformName")}: {productGroup.name}
                  </span>
                  <span className="c-fabric-code">
                    {t("color")}: {productGroup.color}
                  </span>
                </div>
              </div>

              {!collapsed[groupKey] && (
                <table className="c-fabric-inner-table">
                  <thead>
                    <tr>
                      {isEditMode && <th></th>}
                      <th>{t("size")}</th>
                      <th>{t("singlePrice")}</th>
                      <th>{t("requestedQuantity")}</th>
                      <th>{t("receivedQuantity")}</th>
                      <th>{t("finalAmount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productGroup?.sizes?.map((variant) => (
                      <tr key={variant.id}>
                        {isEditMode && (
                          <td>
                            <CustomCheckbox
                              type="checkbox"
                              checked={
                                !!selectedProducts[groupKey]?.[variant.id]
                              }
                              onChange={() =>
                                handleCheckboxChange(groupKey, variant.id)
                              }
                            />
                          </td>
                        )}
                        <td>{variant.size}</td>

                        <td>
                          {isEditMode ? (
                            <CustomInputWithUnit
                              type="moneyV2"
                              value={variant.price}
                              onChange={(e) =>
                                handleInputChange(
                                  groupKey,
                                  variant.id,
                                  "price",
                                  e.target.value
                                )
                              }
                              unit={"IQD"}
                              flex
                            />
                          ) : (
                            `${variant.price} IQD`
                          )}
                        </td>
                        <td>
                          {isEditMode ? (
                            <CustomInputWithUnit
                              type="moneyV2"
                              value={variant.requestedQuantity}
                              onChange={(e) =>
                                handleInputChange(
                                  groupKey,
                                  variant.id,
                                  "requestedQuantity",
                                  e.target.value
                                )
                              }
                              flex
                              unit={t(
                                variant.unitOfMeasure?.toLowerCase() || "piece"
                              )}
                            />
                          ) : (
                            `${variant.requestedQuantity} ${t(
                              variant.unitOfMeasure?.toLowerCase() || "piece"
                            )}`
                          )}
                        </td>
                        <td>
                          {isEditMode ? (
                            <CustomInputWithUnit
                              type="money"
                              value={variant.quantity || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  groupKey,
                                  variant.id,
                                  "quantity",
                                  e.target.value
                                )
                              }
                              flex
                              unit={t("pieces")}
                            />
                          ) : variant.quantity ? (
                            `${variant.quantity} ${t("pieces")}`
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          {isEditMode ? (
                            <CustomInputWithUnit
                              type="money"
                              value={variant.totalPrice}
                              onChange={(e) =>
                                handleInputChange(
                                  groupKey,
                                  variant.id,
                                  "totalPrice",
                                  e.target.value
                                )
                              }
                              flex
                              unit={"IQD"}
                            />
                          ) : variant.totalPrice ? (
                            `${variant.totalPrice} IQD`
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>

      <div className="u-margin--top_500 u-margin--bottom_500 o-row o-row--horizontal-align_end o-row--gap-size_400">
        {!isEditMode && (
          <>
            <CustomButton
              variant="gray"
              title={t("print")}
              onClick={handlePrint}
              icon={printIcon}
              iconSize={18}
            />
            {orderStatus === "PENDING" && (
              <CustomButton
                title={t("orderReceived")}
                variant="green"
                onClick={handleAttemptToConfirmReceived}
                icon={checkIcon}
                iconSize={18}
                disabled={!canConfirmOrder()}
              />
            )}
          </>
        )}

        {areYouSureModal && (
          <AreYouSureModal
            onClose={() => setAreYouSureModal(false)}
            onConfirm={handleConfirmOrder}
            customMessage="areYouSureToConfirmReceivedOrder"
            closeTitle="cancel"
            confirmTitle="confirm"
            loading={loading}
            confirm
            confirmButtonVariant={null}
            confirmIconType="whiteCheck"

          />
        )}
      </div>

      <PrintableUniformOrderView
        ref={printRef}
        data={transformedData}
        currency={selectedCurrency}
        orderDetails={orderDetails}
      />
    </div>
  );
};

export default UniformOrders;
